import React, { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AuthPromoPanel from './AuthPromoPanel';
import RegisterForm from './RegisterForm';
import LoginForm from './LoginForm';
import ActivateForm from './ActivateForm';
import FlikLogo from '../../components/Logo/FlikLogo';
import './AuthenticatePage.css';
import './AuthForm.css';

/**
 * Auth entry point served at `/authenticate`.
 *
 * The active view is driven by the `?tab=` query param so the URL is
 * shareable/bookmarkable and the RequireAuth guard can deep-link into it:
 *   - `?tab=register` (default) → account creation.
 *   - `?tab=login`             → sign in (placeholder for now).
 * After a successful registration the page moves to an internal "activate"
 * step (the emailed OTP), which is flow state rather than a URL tab.
 */
function AuthenticatePage() {
  const [searchParams, setSearchParams] = useNavigateTab();

  const tab = searchParams.get('tab') === 'login' ? 'login' : 'register';
  const [step, setStep] = useState('form'); // 'form' | 'activate'
  const [pendingEmail, setPendingEmail] = useState('');
  // Pre-fills the login field (e.g. right after activating an account).
  const [loginPrefill, setLoginPrefill] = useState('');

  const goToTab = useCallback(
    (nextTab) => {
      setStep('form');
      setSearchParams({ tab: nextTab });
    },
    [setSearchParams]
  );

  const handleRegistered = (email) => {
    setPendingEmail(email);
    setStep('activate');
  };

  const handleActivated = () => {
    // Account confirmed: drop the user on the login tab with their email
    // already filled in so they only need to type their password.
    setLoginPrefill(pendingEmail);
    goToTab('login');
  };

  // A login attempt against an unactivated account (backend answers 403) sends
  // the user straight to the activation step for that identifier.
  const handleNeedsActivation = (identifier) => {
    setPendingEmail(identifier);
    setStep('activate');
  };

  const isActivate = step === 'activate';
  const promoVariant = isActivate ? 'activate' : tab === 'login' ? 'login' : 'register';

  return (
    <div className="AuthPage">
      <AuthPromoPanel variant={promoVariant} showStats={false} />

      <main className="AuthPage-panel">
        <div className="AuthPage-mobileHeader">
          <FlikLogo height={22} />
        </div>

        <div className="AuthForm">
          {!isActivate && (
            <div className="AuthTabs" role="tablist" aria-label="Acceso">
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'login'}
                className={`AuthTab${tab === 'login' ? ' AuthTab--active' : ''}`}
                onClick={() => goToTab('login')}
              >
                Entrar
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'register'}
                className={`AuthTab${tab === 'register' ? ' AuthTab--active' : ''}`}
                onClick={() => goToTab('register')}
              >
                Crear cuenta
              </button>
            </div>
          )}

          {isActivate && (
            <div className="AuthActivateBar">
              <span className="AuthActivateBar-label">Activar cuenta</span>
              <div className="AuthActivateBar-progress">
                <span className="AuthActivateBar-step">Paso 2/3</span>
                <span className="AuthActivateBar-track" aria-hidden="true">
                  <span className="AuthActivateBar-fill" />
                </span>
              </div>
            </div>
          )}

          {isActivate ? (
            <ActivateForm
              email={pendingEmail}
              onActivated={handleActivated}
              onChangeEmail={() => goToTab('register')}
            />
          ) : tab === 'register' ? (
            <RegisterForm
              onRegistered={handleRegistered}
              onSwitchToLogin={() => goToTab('login')}
            />
          ) : (
            <LoginForm
              initialIdentifier={loginPrefill}
              onSwitchToRegister={() => goToTab('register')}
              onNeedsActivation={handleNeedsActivation}
            />
          )}
        </div>
      </main>
    </div>
  );
}

/**
 * Thin wrapper over useSearchParams that guarantees a `tab` is always present
 * in the URL (defaults to `register`), so the two screens never render without
 * a resolved tab.
 */
function useNavigateTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  React.useEffect(() => {
    if (!searchParams.get('tab')) {
      setSearchParams({ tab: 'register' }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return [searchParams, setSearchParams];
}

export default AuthenticatePage;
